import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserCheck, BookOpen, ClipboardList, Newspaper, AlertTriangle, Search, GraduationCap, Briefcase, ShieldCheck } from 'lucide-react';
import { YearContext } from '../components/Layout/Layout';
import StatCard from '../components/Cards/StatCard';
import { AreaChartComponent, BarChartComponent, PieChartComponent } from '../components/Charts/ChartComponents';
import { dashboardAPI, rbacAPI, schoolConfigAPI } from '../services/api';
import { PageLoader } from '../components/Common/LoadingSpinner';
import TeachersDashboard from './TeachersDashboard';

const ROLE_COLORS = {
  super_admin:     'bg-red-100 text-red-700',
  admin:           'bg-orange-100 text-orange-700',
  principal:       'bg-yellow-100 text-yellow-700',
  vice_principal:  'bg-amber-100 text-amber-700',
  coordinator:     'bg-blue-100 text-blue-700',
  class_teacher:   'bg-teal-100 text-teal-700',
  subject_teacher: 'bg-emerald-100 text-emerald-700',
  management:      'bg-purple-100 text-purple-700',
  user:            'bg-slate-100 text-slate-600',
};

function EmployeeSection() {
  const [staff, setStaff]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [typeFilter, setType] = useState('');

  useEffect(() => {
    schoolConfigAPI.getStaff()
      .then(r => setStaff(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = staff.filter(s => {
    const matchSearch = !search ||
      s.name?.toLowerCase().includes(search.toLowerCase()) ||
      s.email?.toLowerCase().includes(search.toLowerCase()) ||
      s.department_name?.toLowerCase().includes(search.toLowerCase()) ||
      s.designation_name?.toLowerCase().includes(search.toLowerCase());
    const matchType = !typeFilter || s.staff_type === typeFilter;
    return matchSearch && matchType;
  });

  const teaching    = staff.filter(s => s.staff_type === 'teacher').length;
  const nonTeaching = staff.filter(s => s.staff_type !== 'teacher').length;
  const withLogin   = staff.filter(s => s.user_id).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Users size={16} className="text-primary-600" /> Employee Directory
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">All staff members registered in the system</p>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Staff',    value: staff.length,  Icon: Users,        color: 'text-primary-600', bg: 'bg-primary-50' },
          { label: 'Teaching',       value: teaching,      Icon: GraduationCap, color: 'text-teal-600',   bg: 'bg-teal-50'    },
          { label: 'Non-Teaching',   value: nonTeaching,   Icon: Briefcase,    color: 'text-orange-600',  bg: 'bg-orange-50'  },
          { label: 'With Login',     value: withLogin,     Icon: ShieldCheck,  color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map(({ label, value, Icon, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl px-4 py-3 flex items-center gap-3`}>
            <div className={`w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm`}>
              <Icon size={15} className={color} />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-800 leading-none">{loading ? '—' : value}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, department…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <select value={typeFilter} onChange={e => setType(e.target.value)}
          className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300">
          <option value="">All Types</option>
          <option value="teacher">Teaching Staff</option>
          <option value="non_teaching">Non-Teaching</option>
          <option value="admin">Admin Staff</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-slate-400 text-sm">Loading employees…</div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center h-24 text-slate-400 text-sm">No staff found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Employee</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Department</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Designation</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Class</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(s => (
                  <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-700 text-xs font-bold">{s.name?.[0]?.toUpperCase() || '?'}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800 leading-tight">{s.name}</p>
                          <p className="text-xs text-slate-400">{s.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{s.department_name || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-slate-600">{s.designation_name || s.designation || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3">
                      {s.role_name ? (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLORS[s.role_name] || 'bg-slate-100 text-slate-600'}`}>
                          {s.role_name.replace(/_/g, ' ')}
                        </span>
                      ) : <span className="text-slate-300 text-xs">No login</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">
                      {s.class_teacher_grade
                        ? `${s.class_teacher_grade}${s.class_teacher_section ? ` – ${s.class_teacher_section}` : ''}`
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                        {s.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!loading && filtered.length > 0 && (
        <p className="text-xs text-slate-400 text-right">{filtered.length} of {staff.length} employees</p>
      )}
    </div>
  );
}

function SchoolDashboardContent() {
  const navigate = useNavigate();
  const { yearId, refreshKey } = useContext(YearContext);
  const [overview, setOverview] = useState(null);
  const [attTrend, setAttTrend] = useState([]);
  const [modSum,   setModSum]   = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dashboardAPI.getOverview({ academicYearId: yearId }),
      dashboardAPI.getAttendanceTrend({ academicYearId: yearId }),
      dashboardAPI.getModuleSummary({ academicYearId: yearId }),
    ]).then(([o, a, m]) => {
      setOverview(o.data);
      setAttTrend(a.data.map(r => ({ month: r.month?.substring(0, 3), pct: r.pct })));
      setModSum(m.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [yearId, refreshKey]);

  if (loading) return <PageLoader />;

  const grievanceStatusData = (modSum?.grievanceStatus || []).map(g => ({ name: g.status, value: g.cnt }));
  const topTeachersData     = (modSum?.topTeachersByLessonCompletion || []).map(t => ({ name: t.teacher.split(' ').slice(-1)[0], avg_pct: t.avg_pct }));
  const hwMonthlyData       = (modSum?.homeworkByMonth || []).map(h => ({
    month: `M${h.month}`,
    pct: h.students > 0 ? Math.round((h.submitted / h.students) * 100) : 0,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard title="Total Employees"    value={overview?.totalTeachers}              icon={Users}         color="blue"   onClick={() => navigate('/teachers')} />
        <StatCard title="Attendance %"       value={`${overview?.attendancePct}%`}        icon={UserCheck}     color="green"  onClick={() => navigate('/attendance')} />
        <StatCard title="Homework %"         value={`${overview?.homeworkCompletionPct}%`}icon={BookOpen}      color="yellow" onClick={() => navigate('/homework')} />
        <StatCard title="Lesson Plan %"      value={`${overview?.lessonCompletionPct}%`}  icon={ClipboardList} color="purple" onClick={() => navigate('/lesson-plan')} />
        <StatCard title="Newsletters"        value={overview?.newsletterCount}            icon={Newspaper}     color="teal"   onClick={() => navigate('/newsletter')} />
        <StatCard title="Pending Grievances" value={overview?.pendingGrievances}          icon={AlertTriangle} color="red"    onClick={() => navigate('/grievances')} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AreaChartComponent
          title="Monthly Attendance Trend" subtitle="Present + Late percentage by month"
          data={attTrend} xKey="month"
          areas={[{ key: 'pct', label: 'Attendance %', color: '#0ea5e9' }]}
        />
        <PieChartComponent
          title="Grievance Status Distribution" subtitle="Current academic year"
          data={grievanceStatusData} nameKey="name" valueKey="value" donut
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BarChartComponent
          title="Monthly Homework Submission %" subtitle="Average submission rate by month"
          data={hwMonthlyData} xKey="month"
          bars={[{ key: 'pct', label: 'Submission %', color: '#f59e0b' }]}
        />
        <BarChartComponent
          title="Top Teachers — Lesson Completion" subtitle="Average completion percentage"
          data={topTeachersData} xKey="name"
          bars={[{ key: 'avg_pct', label: 'Completion %', color: '#8b5cf6' }]}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Overall Performance', value: `${Math.round(((overview?.attendancePct || 0) + (overview?.homeworkCompletionPct || 0) + (overview?.lessonCompletionPct || 0)) / 3)}%`, color: 'bg-gradient-to-br from-primary-500 to-primary-700' },
          { label: 'Active Modules',      value: '5',       color: 'bg-gradient-to-br from-emerald-500 to-emerald-700' },
          { label: 'Data Auto-Refresh',   value: 'Live',    color: 'bg-gradient-to-br from-amber-500 to-amber-700'     },
          { label: 'Telegram Reports',    value: 'Enabled', color: 'bg-gradient-to-br from-purple-500 to-purple-700'   },
        ].map(c => (
          <div key={c.label} className={`${c.color} rounded-2xl p-5 text-white shadow-lg`}>
            <p className="text-3xl font-display font-bold mb-1">{c.value}</p>
            <p className="text-white/80 text-sm">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Employee Directory */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <EmployeeSection />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [view, setView]       = useState(null); // null = loading
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    rbacAPI.getMyDashboard()
      .then(r => setView(r.data.view))
      .catch(() => setView('teacher'))   // safe fallback
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-5 animate-fade-in">
      {view === 'school' && (
        <div>
          <h2 className="text-2xl font-display font-bold text-slate-800">School Dashboard</h2>
          <p className="text-slate-500 text-sm mt-1">Real-time analytics across all school modules</p>
        </div>
      )}
      {view === 'school' ? <SchoolDashboardContent /> : <TeachersDashboard />}
    </div>
  );
}
